import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Table,
  Badge,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import LegalConsultationService from '../../../Api/services/legal-consultation.service';
import { Trash2, Upload, FileText, Download } from 'react-feather';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];
type FileConsulationResponse = components['schemas']['FileConsulationResponse'];

interface DocumentsModalProps {
  isOpen: boolean;
  toggle: () => void;
  consultationId: number;
  consultation?: LegalConsulationResponse | null; // Datos de la consulta (opcional, para evitar petición GET)
  onSuccess: () => void;
}

const DocumentsModal: React.FC<DocumentsModalProps> = ({
  isOpen,
  toggle,
  consultationId,
  consultation: initialConsultation,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [consultation, setConsultation] = useState<LegalConsulationResponse | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [typeFile, setTypeFile] = useState<string>('N'); // N->Normal, I->AI

  useEffect(() => {
    if (isOpen && consultationId) {
      // Resetear el estado cuando se abre el modal
      setSelectedFile(null);
      setTypeFile('N');
      
      // Si tenemos los datos de la consulta inicialmente, usarlos directamente
      if (initialConsultation && initialConsultation.id === consultationId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📥 DocumentsModal - Usando datos iniciales de la consulta:', {
            consultationId: initialConsultation.id,
            filesCount: initialConsultation.files?.length || 0,
          });
        }
        setConsultation(initialConsultation);
      } else {
        // Si no tenemos los datos, intentar cargarlos
        setConsultation(null);
        loadConsultation();
      }
    }
  }, [isOpen, consultationId, initialConsultation]);

  const loadConsultation = async (silent = false) => {
    if (!consultationId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ DocumentsModal - No hay consultationId para cargar');
      }
      return;
    }

    try {
      setLoading(true);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 DocumentsModal - Cargando consulta:', { consultationId });
      }
      
      const data = await LegalConsultationService.getConsultationById(consultationId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ DocumentsModal - Consulta cargada:', {
          consultationId: data.id,
          filesCount: data.files?.length || 0,
          files: data.files?.map(f => ({ id: f.id, file_type: f.file_type, url: f.url })) || [],
        });
      }
      
      setConsultation(data);
    } catch (error: any) {
      // Si es un error 418 o CORS, solo loggear en desarrollo, no mostrar error al usuario
      // Estos errores generalmente son problemas del servidor, no del cliente
      if (error.response?.status === 418 || error.message?.includes('CORS')) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ Error al recargar consulta (418/CORS):', error);
        }
        // No mostrar error al usuario si es silencioso o si es un error del servidor
        if (!silent) {
          // Intentar usar los datos que ya tenemos si existen
          if (consultation) {
            return; // Mantener los datos existentes
          }
        }
        return;
      }
      
      // Para otros errores, mostrar mensaje solo si no es silencioso
      if (!silent) {
        const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar documentos');
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e?: React.MouseEvent) => {
    // Prevenir comportamiento por defecto si viene de un evento
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!selectedFile) {
      toast.warning(t('Por favor selecciona un archivo'));
      return;
    }

    if (!consultationId) {
      toast.error(t('ID de consulta no válido'));
      return;
    }

    // Debug: verificar datos antes de enviar
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 DocumentsModal - Iniciando subida:', {
        consultationId,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        typeFile,
      });
    }

    try {
      setUploading(true);
      const result = await LegalConsultationService.addDocument(consultationId, selectedFile, typeFile);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ DocumentsModal - Documento agregado exitosamente:', result);
      }

      toast.success(t('Documento agregado exitosamente'));
      
      // Actualizar el estado directamente con la respuesta del servidor
      // Esto evita tener que recargar y potenciales errores 418/CORS
      if (result) {
        setConsultation(result);
      } else {
        // Si no viene en la respuesta, intentar recargar de forma silenciosa
        await loadConsultation(true);
      }
      
      setSelectedFile(null);
      setTypeFile('N');
      // Limpiar el input
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
      onSuccess();
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ DocumentsModal - Error al agregar documento:', error);
        console.error('❌ DocumentsModal - Detalles del error:', {
          status: error.response?.status,
          data: error.response?.data,
          detail: error.response?.data?.detail,
          validationErrors: error.response?.data?.detail,
        });
      }
      
      // Manejar diferentes tipos de errores
      let errorMessage = t('Error al agregar documento');
      
      if (error.response?.status === 422) {
        // Error de validación - mostrar detalles específicos
        const validationError = error.response?.data;
        if (validationError?.detail) {
          if (Array.isArray(validationError.detail)) {
            // Errores de validación en formato FastAPI
            const errors = validationError.detail.map((err: any) => 
              `${err.loc?.join('.')}: ${err.msg}`
            ).join(', ');
            errorMessage = t('Error de validación') + ': ' + errors;
          } else if (typeof validationError.detail === 'string') {
            errorMessage = validationError.detail;
          } else {
            errorMessage = JSON.stringify(validationError.detail);
          }
        } else if (validationError?.message) {
          errorMessage = validationError.message;
        } else {
          errorMessage = t('Error de validación: El formato del archivo o los parámetros no son válidos');
        }
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: number) => {
    if (!window.confirm(t('¿Estás seguro de eliminar este documento?'))) {
      return;
    }

    try {
      setLoading(true);
      await LegalConsultationService.deleteDocument(consultationId, fileId);
      toast.success(t('Documento eliminado exitosamente'));
      loadConsultation();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al eliminar documento');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url: string | null) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.warning(t('URL del documento no disponible'));
    }
  };

  const getFileTypeBadge = (fileType: string) => {
    return fileType === 'I' ? (
      <Badge color="info">{t('AI')}</Badge>
    ) : (
      <Badge color="secondary">{t('Normal')}</Badge>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{t('Gestión de Documentos')}</ModalHeader>
      <ModalBody>
        {loading && !consultation ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            {/* Formulario de carga */}
            <div className="mb-4 p-3 border rounded">
              <h6 className="mb-3">{t('Agregar Nuevo Documento')}</h6>
              <FormGroup>
                <Label>{t('Tipo de Archivo')}</Label>
                <Input
                  type="select"
                  value={typeFile}
                  onChange={(e) => setTypeFile(e.target.value)}
                >
                  <option value="N">{t('Normal')}</option>
                  <option value="I">{t('AI')}</option>
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>{t('Seleccionar Archivo')}</Label>
                <Input
                  id="file-input"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
              </FormGroup>
              <Btn
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔘 Botón Subir Documento clickeado', {
                    selectedFile: selectedFile?.name,
                    consultationId,
                    typeFile,
                    uploading,
                  });
                  handleUpload(e);
                }}
                disabled={!selectedFile || uploading || !consultationId}
                className="w-100"
                type="button"
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t('Subiendo...')}
                  </>
                ) : (
                  <>
                    <Upload size={16} className="me-2" />
                    {t('Subir Documento')}
                  </>
                )}
              </Btn>
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-2 small text-muted">
                  Debug: Archivo seleccionado: {selectedFile ? selectedFile.name : 'Ninguno'} | 
                  ID Consulta: {consultationId || 'N/A'} | 
                  Tipo: {typeFile}
                </div>
              )}
            </div>

            {/* Lista de documentos */}
            <div>
              <h6 className="mb-3">
                {t('Documentos Existentes')} 
                {consultation?.id && (
                  <small className="text-muted ms-2">
                    ({t('Consulta')} #{consultation.id})
                  </small>
                )}
              </h6>
              {consultation?.files && consultation.files.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>{t('Tipo')}</th>
                        <th>{t('Fecha')}</th>
                        <th>{t('Acciones')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consultation.files
                        .filter((file: FileConsulationResponse) => {
                          // Validar que el archivo tiene datos válidos
                          if (!file || file.id === null || file.id === undefined) {
                            if (process.env.NODE_ENV === 'development') {
                              console.warn('⚠️ Archivo inválido filtrado:', file);
                            }
                            return false;
                          }
                          return true;
                        })
                        .map((file: FileConsulationResponse) => (
                        <tr key={file.id}>
                          <td>{getFileTypeBadge(file.file_type)}</td>
                          <td>
                            {file.created_at
                              ? new Date(file.created_at).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '-'}
                          </td>
                          <td>
                            <div className="d-flex">
                              {file.url && (
                                <Btn
                                  color="info"
                                  size="sm"
                                  className="me-1 btn_list_action"
                                  onClick={() => handleDownload(file.url)}
                                  title={t('Descargar')}
                                >
                                  <Download size={14} />
                                </Btn>
                              )}
                              <Btn
                                color="danger"
                                size="sm"
                                className="btn_list_action"
                                onClick={() => file.id && handleDelete(file.id)}
                                disabled={loading}
                                title={t('Eliminar')}
                              >
                                <Trash2 size={14} />
                              </Btn>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted text-center py-3">
                  <FileText size={48} className="mb-2 d-block mx-auto opacity-50" />
                  {t('No hay documentos asociados a esta consulta')}
                </p>
              )}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Btn color="secondary" onClick={toggle} disabled={loading || uploading}>
          {t('Cerrar')}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

export default DocumentsModal;

